/**
 * RuntimeFormBody
 *
 * Headless runtime form renderer. Contains all the schema-resolution, data-
 * seeding, validation, and submission logic — but no page chrome (no Layout,
 * no full-page back button). Designed to be embedded inside:
 *
 *   • RuntimeFormPage  — full-page wrapper that adds <Layout> chrome
 *   • RuntimeFormDrawer — workspace-side drawer overlay
 *
 * The drawer mode (`compact`) hides the page-instruction banner and uses a
 * slimmer header so it fits the right-side overlay rail.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Save, X } from 'lucide-react';

import {
  AppCard,
  AppCardContent,
  AppCardHeader,
  AppCardTitle,
} from '@/modules/shared/components/ui/AppCard';
import { Button } from '@/modules/shared/components/ui/button';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import { FormErrorBanner } from '@/modules/shared/components/ui/FormErrorBanner';
import { FormSkeleton } from '@/modules/shared/components/ui/AppSkeleton';
import { PageInstructionBanner } from '@/modules/shared/components/PageInstructionBanner';
import { useToast } from '@/modules/shared/components/ui/toast';

import { useEntityFormResolution } from '../../hooks/useEntityFormResolution';
import { useConditionalLogic } from '../../hooks/useConditionalLogic';
import { EntityFormErrorState } from '../../components/shared/EntityFormErrorState';
import { DynamicFormField } from '../../components/workspace/DynamicFormField';
import {
  getFieldDefaultValue,
  hasHospitalEntityPicker,
  validateFieldValue,
  type FieldValue,
} from '../../components/workspace/dynamicFormFieldUtils';
import { evaluateCrossFieldRules } from '../../components/form-builder/state/crossFieldValidation';
import { workspaceService } from '../../services/workspaceService';
import { runtimeFormService } from '../../services/runtimeFormService';
import type { FormFieldDefinition } from '../../types/formBuilder';
import type {
  WorkspaceHcpNodeDto,
  WorkspaceHospitalNodeDto,
  WorkspaceOverviewResponseDto,
  WorkspacePatientNodeDto,
} from '../../types/workspace';
import {
  CONTEXT_MAP,
  isValidContext,
  isValidMode,
  type RuntimeContextKey,
} from '../../runtime/contextMap';
import { evaluateVisibilityCondition } from '../../runtime/stepVisibility';
import {
  getRecommendedNextStep,
  orderStepsDynamically,
  type RuntimeFormStep,
} from '../../runtime/FormPathEngine';
import {
  normalizeHospitalParentIds,
  normalizePatientParentIds,
  withLegacyEntityParentKeys,
} from '../../runtime/patientParentLinkage';

type RuntimeFormSubmitVariables = Parameters<typeof runtimeFormService.submit>[0];
type WorkspaceOverviewApiResponse = {
  success: boolean;
  data?: WorkspaceOverviewResponseDto | null;
  message?: string;
  errors?: unknown[];
  correlationId?: string;
};
type OptimisticRuntimeEntity = {
  tempId: string;
  context: RuntimeContextKey;
  formData: Record<string, FieldValue>;
  parentIds: Record<string, string>;
};

type RuntimeGoToStepSource = 'step-chip' | 'next' | 'back' | 'system';

function normalizeRuntimeDateValue(value: FieldValue): FieldValue {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;

  const ddMmYyyy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(trimmed);
  if (ddMmYyyy) {
    const [, dd, mm, yyyy] = ddMmYyyy;
    return `${yyyy}-${mm}-${dd}`;
  }

  const isoPrefix = /^(\d{4}-\d{2}-\d{2})T/.exec(trimmed);
  if (isoPrefix) return isoPrefix[1];

  return value;
}

const displayValue = (formData: Record<string, FieldValue>, candidates: string[], fallback: string) => {
  for (const key of candidates) {
    const value = formData[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return fallback;
};

const createOptimisticHospital = ({ tempId, formData }: OptimisticRuntimeEntity): WorkspaceHospitalNodeDto => ({
  hospitalId: tempId,
  hospitalName: displayValue(formData, ['hospitalName', 'name', 'facilityName'], 'New hospital'),
  hospitalCode: 'Pending',
  status: 'Pending',
  currentWorkflowStep: 'Saving',
  hcpCount: 0,
  patientCount: 0,
  createdAt: new Date().toISOString(),
  dynamicFields: formData,
  hcps: [],
  isExpanded: true,
  isOptimistic: true,
});

const createOptimisticHcp = ({ tempId, formData }: OptimisticRuntimeEntity): WorkspaceHcpNodeDto => ({
  hcpId: tempId,
  hcpName: displayValue(formData, ['hcpName', 'fullName', 'name', 'doctorName'], 'New HCP'),
  hcpCode: 'Pending',
  status: 'Pending',
  currentWorkflowStep: 'Saving',
  patientCount: 0,
  createdAt: new Date().toISOString(),
  dynamicFields: formData,
  patients: [],
  isExpanded: true,
  isOptimistic: true,
});

const createOptimisticPatient = ({ tempId, formData }: OptimisticRuntimeEntity): WorkspacePatientNodeDto => ({
  patientId: tempId,
  patientName: displayValue(formData, ['patientName', 'fullName', 'name'], 'New patient'),
  patientCode: 'Pending',
  status: 'Pending',
  currentWorkflowStep: 'Saving',
  createdAt: new Date().toISOString(),
  dynamicFields: formData,
  hasActiveAssistance: false,
  caseCount: 0,
  assistanceCases: [],
  isExpanded: true,
  isOptimistic: true,
});

const replaceOptimisticEntity = (
  overview: WorkspaceOverviewApiResponse | undefined,
  tempId: string,
  entityId: string,
): WorkspaceOverviewApiResponse | undefined => {
  if (!overview?.data?.list?.items) return overview;
  return {
    ...overview,
    data: {
      ...overview.data,
      list: {
        ...overview.data.list,
        items: overview.data.list.items.map((hospital) => {
          if (hospital.hospitalId === tempId) {
            return {
              ...hospital,
              hospitalId: entityId,
              status: 'Active',
              currentWorkflowStep: 'Created',
              isOptimistic: false,
            };
          }
          return {
            ...hospital,
            hcps: hospital.hcps.map((hcp) => {
              if (hcp.hcpId === tempId) {
                return {
                  ...hcp,
                  hcpId: entityId,
                  status: 'Active',
                  currentWorkflowStep: 'Created',
                  isOptimistic: false,
                };
              }
              return {
                ...hcp,
                patients: hcp.patients.map((patient) => patient.patientId === tempId
                  ? {
                      ...patient,
                      patientId: entityId,
                      status: 'Active',
                      currentWorkflowStep: 'Created',
                      isOptimistic: false,
                    }
                  : patient),
              };
            }),
          };
        }),
      },
    },
  };
};

const markEditedEntityOptimistic = (
  overview: WorkspaceOverviewApiResponse | undefined,
  variables: RuntimeFormSubmitVariables,
  isOptimistic: boolean,
): WorkspaceOverviewApiResponse | undefined => {
  if (!overview?.data?.list?.items || !variables.entityId) return overview;
  const { context, entityId, formData } = variables;
  return {
    ...overview,
    data: {
      ...overview.data,
      list: {
        ...overview.data.list,
        items: overview.data.list.items.map((hospital) => {
          if (context === 'hospital' && hospital.hospitalId === entityId) {
            return {
              ...hospital,
              hospitalName: displayValue(formData, ['hospitalName', 'name', 'facilityName'], hospital.hospitalName),
              dynamicFields: { ...hospital.dynamicFields, ...formData },
              isOptimistic,
            };
          }
          return {
            ...hospital,
            hcps: hospital.hcps.map((hcp) => {
              if (context === 'hcp' && hcp.hcpId === entityId) {
                return {
                  ...hcp,
                  hcpName: displayValue(formData, ['hcpName', 'fullName', 'name', 'doctorName'], hcp.hcpName),
                  dynamicFields: { ...hcp.dynamicFields, ...formData },
                  isOptimistic,
                };
              }
              return {
                ...hcp,
                patients: hcp.patients.map((patient) => (
                  context === 'patient' && patient.patientId === entityId
                    ? {
                        ...patient,
                        patientName: displayValue(formData, ['patientName', 'fullName', 'name'], patient.patientName),
                        dynamicFields: { ...patient.dynamicFields, ...formData },
                        isOptimistic,
                      }
                    : patient
                )),
              };
            }),
          };
        }),
      },
    },
  };
};

const addOptimisticEntity = (
  overview: WorkspaceOverviewApiResponse | undefined,
  optimistic: OptimisticRuntimeEntity,
): WorkspaceOverviewApiResponse | undefined => {
  if (!overview?.data?.list?.items) return overview;
  const items = overview.data.list.items;
  let nextItems = items;
  if (optimistic.context === 'hospital') {
    nextItems = [createOptimisticHospital(optimistic), ...items];
  }
  if (optimistic.context === 'hcp') {
    const hospitalId = optimistic.parentIds.hospitalId;
    nextItems = items.map((hospital) => hospital.hospitalId === hospitalId
      ? {
          ...hospital,
          hcpCount: hospital.hcpCount + 1,
          hcps: [createOptimisticHcp(optimistic), ...hospital.hcps],
          isExpanded: true,
        }
      : hospital);
  }
  if (optimistic.context === 'patient') {
    const hcpId = optimistic.parentIds.hcpId;
    nextItems = items.map((hospital) => ({
      ...hospital,
      patientCount: hospital.hcps.some((hcp) => hcp.hcpId === hcpId)
        ? hospital.patientCount + 1
        : hospital.patientCount,
      hcps: hospital.hcps.map((hcp) => hcp.hcpId === hcpId
        ? {
            ...hcp,
            patientCount: hcp.patientCount + 1,
            patients: [createOptimisticPatient(optimistic), ...hcp.patients],
            isExpanded: true,
          }
        : hcp),
    }));
  }
  return {
    ...overview,
    data: {
      ...overview.data,
      list: {
        ...overview.data.list,
        items: nextItems,
        totalCount: optimistic.context === 'hospital'
          ? overview.data.list.totalCount + 1
          : overview.data.list.totalCount,
      },
    },
  };
};

export interface RuntimeFormBodyProps {
  programId: string;
  /** Raw context from URL or query string (e.g. 'hospital'). */
  contextRaw: string | undefined;
  /** Raw mode from URL or query string ('create' | 'edit' | 'new' | …). */
  modeRaw: string | undefined;
  /** Existing entity id, required for edit mode. */
  entityId?: string;
  /** Parent ids (hospitalId, hcpId, …) sourced from query string. */
  parentIds: Record<string, string>;
  /** Called when the user cancels OR after a successful submit (drawer close, page navigate-back). */
  onClose: () => void;
  /** Optional success hook for parent (e.g. workspace refetch). Fires before onClose. */
  onSuccess?: () => void;
  /** Drawer mode hides the instruction banner and tightens the chrome. */
  compact?: boolean;
}

export function RuntimeFormBody({
  programId,
  contextRaw,
  modeRaw,
  entityId,
  parentIds,
  onClose,
  onSuccess,
  compact = false,
}: RuntimeFormBodyProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Normalize aliases: 'new' → 'create', 'update' → 'edit'.
  const normalizedMode = useMemo(() => {
    if (!modeRaw) return undefined;
    const m = modeRaw.toLowerCase();
    if (m === 'new') return 'create';
    if (m === 'update') return 'edit';
    return m;
  }, [modeRaw]);

  const contextKey = contextRaw as RuntimeContextKey | undefined;
  const validContext = isValidContext(contextKey);
  const validMode = isValidMode(normalizedMode);
  const isEditMode = validMode && normalizedMode === 'edit';

  const config = validContext ? CONTEXT_MAP[contextKey!] : null;

  // ── Form state ───────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dataSeeded, setDataSeeded] = useState(false);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [isBodyScrolled, setIsBodyScrolled] = useState(false);
  const [formError, setFormError] = useState<{ message: string; correlationId?: string } | null>(null);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Reset state when (context, mode, entityId) changes — important for the
  // drawer which stays mounted across multiple openings.
  useEffect(() => {
    setFormData({});
    setErrors({});
    setDataSeeded(false);
    setFormError(null);
  }, [contextKey, normalizedMode, entityId]);

  // ── Resolve form schema ──────────────────────────────────────────────────
  const {
    resolution,
    formSchema,
    formId,
    formVersion,
    isLoading: isFormLoading,
    error: formResolutionError,
    refetch: refetchFormResolution,
    noFormAvailable,
  } = useEntityFormResolution({
    programId: programId ?? '',
    entityType: (config?.entityType ?? 'HOSPITAL') as 'HOSPITAL' | 'HCP' | 'PATIENT',
    actionType: isEditMode ? 'EDIT' : 'CREATE',
    enabled: !!programId && validContext && config?.entityType !== null,
  });

  // ── Load existing entity (edit) ──────────────────────────────────────────
  const entityDetailQuery = useQuery({
    queryKey: ['runtime', 'entity', contextKey, programId, entityId],
    queryFn: async () => {
      if (!programId || !entityId || !contextKey) return null;
      switch (contextKey) {
        case 'hospital':
          return workspaceService.getHospitalDetail(programId, entityId);
        case 'hcp':
          return workspaceService.getHcpDetail(programId, entityId);
        case 'patient':
          return workspaceService.getPatientDetail(programId, entityId);
        default:
          return null;
      }
    },
    enabled: isEditMode && !!entityId && !!programId && !!contextKey,
  });

  const isEntityLoading = entityDetailQuery.isLoading && isEditMode;
  const isLoading = isFormLoading || isEntityLoading;

  // ── Seed form data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!formSchema || !Array.isArray(formSchema.fields) || dataSeeded) return;

    if (isEditMode) {
      const payload = entityDetailQuery.data?.data as
        | { formData?: Record<string, FieldValue> }
        | undefined;
      if (payload?.formData) {
        setFormData(payload.formData);
        setDataSeeded(true);
      }
    } else {
      const defaults: Record<string, FieldValue> = {};
      formSchema.fields.forEach((field) => {
        defaults[field.internalName] = getFieldDefaultValue(field);
      });
      setFormData(defaults);
      setDataSeeded(true);
    }
  }, [formSchema, isEditMode, entityDetailQuery.data, dataSeeded]);

  // ── Conditional logic ────────────────────────────────────────────────────
  const logic = useConditionalLogic(Array.isArray(formSchema?.fields) ? formSchema.fields : undefined, formData);

  const isFieldVisible = useCallback(
    (field: FormFieldDefinition) =>
      logic.isVisible(field.fieldId)
      && evaluateVisibilityCondition(field.visibilityCondition, formData),
    [logic, formData],
  );

  const getVisibleFields = useCallback(
    (fields: FormFieldDefinition[]) => fields.filter(isFieldVisible),
    [isFieldVisible],
  );

  const dependencyFieldKeyMap = useMemo(() => {
    const map: Record<string, string> = {};
    (formSchema?.fields ?? []).forEach((f) => {
      map[f.fieldId] = f.internalName;
      map[f.internalName] = f.internalName;
    });
    return map;
  }, [formSchema?.fields]);

  const hasBuiltInHospitalSelector = useMemo(
    () => hasHospitalEntityPicker(formSchema?.fields),
    [formSchema?.fields],
  );

  const shouldRenderHospitalSelector = !isEditMode
    && (contextKey === 'hcp' || contextKey === 'pharmacy')
    && !hasBuiltInHospitalSelector;

  const runtimeHospitalSelectorField = useMemo<FormFieldDefinition>(() => ({
    fieldId: '__runtime_hospital_selector__',
    label: 'Hospital',
    internalName: 'hospitalId',
    fieldType: 'ENTITY_SELECTOR',
    dataType: 'STRING',
    mandatory: true,
    sortOrder: -100,
    placeholder: 'Select hospital...',
    entitySelectorConfig: {
      entityType: 'HOSPITAL',
      multiSelect: false,
      searchEnabled: true,
      lazyLoading: true,
      autoMappingEnabled: false,
      hierarchyEnforcement: false,
    },
  }), []);

  useEffect(() => {
    if (!dataSeeded || isEditMode) return;
    if (contextKey !== 'hcp' && contextKey !== 'pharmacy') return;
    const preselectedHospitalId = parentIds.hospitalId ?? parentIds.Hospital;
    if (!preselectedHospitalId) return;

    setFormData((prev) => {
      if (prev.hospitalId) return prev;
      return {
        ...prev,
        hospitalId: preselectedHospitalId,
      };
    });
  }, [contextKey, dataSeeded, isEditMode, parentIds]);

  // Apply forced values from SET_VALUE rules back into formData
  useEffect(() => {
    const forced = logic.forcedValues;
    if (!Object.keys(forced).length) return;
    setFormData((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [key, val] of Object.entries(forced)) {
        if (prev[key] !== val) {
          next[key] = val;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [logic.forcedValues]);

  // ── Field change ─────────────────────────────────────────────────────────
  // Collects every field transitively dependent on `fieldName` (direct children,
  // their children, etc.) by walking parentDependencyFieldId edges. A Hospital
  // change must also invalidate Patient even though Patient's parent is HCP, not
  // Hospital directly.
  const collectTransitiveDependents = useCallback((fieldName: string): Set<string> => {
    const dependents = new Set<string>();
    if (!formSchema?.fields) return dependents;
    const changedField = formSchema.fields.find((f) => f.internalName === fieldName);
    const changedFieldId = changedField?.fieldId;

    const frontier = [fieldName, ...(changedFieldId ? [changedFieldId] : [])];
    const visitedKeys = new Set(frontier);

    while (frontier.length > 0) {
      const key = frontier.pop()!;
      formSchema.fields.forEach((candidate) => {
        if (candidate.fieldType !== 'ENTITY_SELECTOR') return;
        if (dependents.has(candidate.internalName)) return;
        const dependency = candidate.entitySelectorConfig?.parentDependencyFieldId;
        if (!dependency || dependency !== key) return;
        dependents.add(candidate.internalName);
        const nextKeys = [candidate.internalName, candidate.fieldId].filter(
          (k): k is string => !!k && !visitedKeys.has(k),
        );
        nextKeys.forEach((k) => visitedKeys.add(k));
        frontier.push(...nextKeys);
      });
    }

    dependents.delete(fieldName);
    return dependents;
  }, [formSchema?.fields]);

  const handleFieldChange = useCallback((fieldName: string, value: FieldValue) => {
    const changedField = formSchema?.fields?.find((f) => f.internalName === fieldName);
    const normalizedValue = changedField?.fieldType === 'DATE'
      ? normalizeRuntimeDateValue(value)
      : value;

    const dependents = collectTransitiveDependents(fieldName);

    setFormError(null);
    setFormData((prev) => {
      const next = { ...prev, [fieldName]: normalizedValue };

      dependents.forEach((dependentName) => {
        if (next[dependentName] !== null && next[dependentName] !== undefined && next[dependentName] !== '') {
          next[dependentName] = null;
        }
      });

      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      let changed = false;

      if (next[fieldName]) {
        delete next[fieldName];
        changed = true;
      }

      dependents.forEach((dependentName) => {
        if (next[dependentName]) {
          delete next[dependentName];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [formSchema?.fields, collectTransitiveDependents]);

  // ── Validation (scoped to a subset of fields, or whole form when omitted) ──
  const collectValidationErrors = useCallback(
    (scopeFields?: FormFieldDefinition[]): Record<string, string> => {
      if (!formSchema) return { __form: 'Form configuration is not available.' };
      if (!Array.isArray(formSchema.fields)) {
        console.error('formSchema.fields is not an array:', formSchema.fields);
        return { __form: 'Form configuration is invalid (fields not an array).' };
      }
      const next: Record<string, string> = {};
      const fields = scopeFields ?? formSchema.fields;
      const scopedIds = new Set(fields.map((f) => f.fieldId));

      fields.forEach((field) => {
        if (!isFieldVisible(field)) return;
        const required = logic.isRequired(field);
        if (!required) return;
        const msg = validateFieldValue(
          { ...field, mandatory: true },
          formData[field.internalName],
        );
        if (msg) next[field.internalName] = msg;
      });

      let crossRules: Parameters<typeof evaluateCrossFieldRules>[0] = [];
      const raw = formSchema.properties?.crossFieldRules;
      if (raw) {
        try {
          crossRules = JSON.parse(String(raw));
        } catch {
          crossRules = [];
        }
      }
      if (crossRules.length) {
        const byFieldId: Record<string, unknown> = {};
        formSchema.fields.forEach((f) => {
          byFieldId[f.fieldId] = formData[f.internalName];
        });
        const violations = evaluateCrossFieldRules(crossRules, byFieldId);
        violations.forEach((v) => {
          // When scoped, only surface violations whose fields all sit inside the scope.
          if (scopeFields && !v.fieldIds.every((fid) => scopedIds.has(fid))) return;
          v.fieldIds.forEach((fid) => {
            const match = formSchema.fields.find((f) => f.fieldId === fid);
            if (match && !next[match.internalName]) {
              next[match.internalName] = v.message;
            }
          });
        });
      }

      return next;
    },
    [formSchema, formData, logic, isFieldVisible],
  );

  const applyValidationErrors = useCallback((next: Record<string, string>, scopeFields?: FormFieldDefinition[]) => {
    setErrors((prev) => {
      if (!scopeFields) return next;
      const merged = { ...prev };
      scopeFields.forEach((field) => {
        delete merged[field.internalName];
      });
      return { ...merged, ...next };
    });
  }, []);

  // ── Submit ───────────────────────────────────────────────────────────────
  const submitMutation = useMutation({
    mutationFn: runtimeFormService.submit,
    onMutate: async (variables: RuntimeFormSubmitVariables) => {
      const queryFilter = {
        predicate: (query: Query) =>
          query.queryKey[0] === 'workspace-overview' && query.queryKey[1] === variables.programId,
      };

      await queryClient.cancelQueries(queryFilter);
      const snapshots = queryClient.getQueriesData<WorkspaceOverviewApiResponse>(queryFilter);
      if (variables.mode === 'create') {
        const tempId = `optimistic-${Date.now()}`;
        queryClient.setQueriesData<WorkspaceOverviewApiResponse>(queryFilter, (old) =>
          addOptimisticEntity(old, {
            tempId,
            context: variables.context,
            formData: variables.formData,
            parentIds: variables.parentIds ?? {},
          }),
        );
        return { tempId, snapshots };
      }
      queryClient.setQueriesData<WorkspaceOverviewApiResponse>(queryFilter, (old) =>
        markEditedEntityOptimistic(old, variables, true),
      );
      return { tempId: null, snapshots };
    },
    onSuccess: (res, variables, context) => {
      if (!res.success) {
        context?.snapshots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
        const errMsg = res.message || 'Submission failed.';
        setFormError({ message: errMsg, correlationId: res.correlationId });
        return;
      }
      setFormError(null);
      if (context?.tempId && res.data?.entityId) {
        queryClient.setQueriesData<WorkspaceOverviewApiResponse>(
          {
            predicate: (query) =>
              query.queryKey[0] === 'workspace-overview' && query.queryKey[1] === variables.programId,
          },
          (old) => replaceOptimisticEntity(old, context.tempId!, res.data.entityId),
        );
      }
      if (variables.mode === 'edit') {
        queryClient.setQueriesData<WorkspaceOverviewApiResponse>(
          {
            predicate: (query) =>
              query.queryKey[0] === 'workspace-overview' && query.queryKey[1] === variables.programId,
          },
          (old) => markEditedEntityOptimistic(old, variables, false),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-overview', programId] });
      showToast(
        `${config?.label ?? 'Record'} ${isEditMode ? 'updated' : 'created'} successfully.`,
        'success',
      );
      onSuccess?.();
      onClose();
    },
    onError: (err: unknown, _variables, context) => {
      context?.snapshots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      const resp = (err as { response?: { data?: { message?: string; correlationId?: string } } })
        ?.response?.data;
      const msg = resp?.message ?? 'Submission failed.';
      setFormError({ message: msg, correlationId: resp?.correlationId });
    },
  });

  // ── Multi-step machinery ─────────────────────────────────────────────────
  // A form becomes a runtime wizard when its schema declares 2+ step sections.
  // Fields without a sectionId fall into a synthetic, always-visible "General" step.
  const allSteps = useMemo<RuntimeFormStep[]>(() => {
    if (!formSchema || !Array.isArray(formSchema.fields)) return [];
    const declared = (formSchema.sections ?? [])
      .filter((section) => section.isStep !== false)
      .slice()
      .sort((a, b) =>
        (a.baseOrder ?? a.stepOrder ?? a.sortOrder ?? 0)
        - (b.baseOrder ?? b.stepOrder ?? b.sortOrder ?? 0),
      );

    const fieldsBySection = new Map<string, FormFieldDefinition[]>();
    const orphanFields: FormFieldDefinition[] = [];
    [...formSchema.fields]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .forEach((f) => {
        if (f.sectionId && declared.some((s) => s.sectionId === f.sectionId)) {
          const list = fieldsBySection.get(f.sectionId) ?? [];
          list.push(f);
          fieldsBySection.set(f.sectionId, list);
        } else {
          orphanFields.push(f);
        }
      });

    const result: RuntimeFormStep[] = [];
    if (orphanFields.length) {
      result.push({
        section: {
          sectionId: '__general__',
          title: 'General',
          sortOrder: -1,
          isStep: true,
          stepOrder: -1,
          baseOrder: -1,
        },
        fields: orphanFields,
      });
    }
    declared.forEach((sec) => {
      const fs = fieldsBySection.get(sec.sectionId) ?? [];
      if (fs.length) result.push({ section: sec, fields: fs });
    });
    return result;
  }, [formSchema]);

  const visibleSteps = useMemo(
    () => orderStepsDynamically(
      allSteps
        .filter((step) => evaluateVisibilityCondition(step.section.visibilityCondition, formData))
        .map((step) => ({ ...step, fields: getVisibleFields(step.fields) }))
        .filter((step) => step.fields.length > 0),
      formData,
    ),
    [allSteps, formData, getVisibleFields],
  );

  const isMultiStep = allSteps.length >= 2;
  const [currentStep, setCurrentStep] = useState(0);
  const [completedStepIds, setCompletedStepIds] = useState<Set<string>>(new Set());

  // Reset step state when schema/mode/entity changes.
  useEffect(() => {
    setCurrentStep(0);
    setActiveStepId(null);
    // In edit mode, treat all schema steps as previously completed so users can jump freely.
    setCompletedStepIds(isEditMode ? new Set(allSteps.map((s) => s.section.sectionId)) : new Set());
  }, [formSchema, isEditMode, allSteps.length]);

  useEffect(() => {
    if (!isMultiStep) return;
    const fallbackStep = visibleSteps[0]?.section.sectionId ?? null;
    setActiveStepId((prev) => {
      if (prev && visibleSteps.some((step) => step.section.sectionId === prev)) return prev;
      return fallbackStep;
    });
  }, [isMultiStep, visibleSteps]);

  useEffect(() => {
    if (!isMultiStep) return;
    const nextIndex = visibleSteps.findIndex((step) => step.section.sectionId === activeStepId);
    setCurrentStep(nextIndex >= 0 ? nextIndex : 0);
  }, [isMultiStep, visibleSteps, activeStepId]);

  const visibleFieldNames = useMemo(
    () => new Set(
      isMultiStep
        ? visibleSteps.flatMap((step) => step.fields.map((field) => field.internalName))
        : getVisibleFields(Array.isArray(formSchema?.fields) ? formSchema.fields : []).map((field) => field.internalName),
    ),
    [isMultiStep, visibleSteps, getVisibleFields, formSchema?.fields],
  );

  useEffect(() => {
    setErrors((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([fieldName]) => visibleFieldNames.has(fieldName)),
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [visibleFieldNames]);

  const activeStep = isMultiStep ? visibleSteps[currentStep] : undefined;
  const activeStepFields = activeStep?.fields ?? [];
  const totalSteps = visibleSteps.length;
  const recommendedStep = useMemo(
    () => getRecommendedNextStep(visibleSteps, formData, completedStepIds),
    [visibleSteps, formData, completedStepIds],
  );
  const recommendedStepId = recommendedStep?.section.sectionId;
  const progressPct = isMultiStep
    ? Math.round(((currentStep + 1) / Math.max(totalSteps, 1)) * 100)
    : 0;

  const registerFieldRef = useCallback((fieldKey: string, node: HTMLDivElement | null) => {
    fieldRefs.current[fieldKey] = node;
  }, []);

  const scrollToFirstError = useCallback((nextErrors: Record<string, string>) => {
    const firstKey = Object.keys(nextErrors).find((key) => key !== '__form');
    if (!firstKey) return;

    if (isMultiStep) {
      const errorStep = visibleSteps.find((step) =>
        step.fields.some((field) => field.internalName === firstKey),
      );
      if (errorStep) setActiveStepId(errorStep.section.sectionId);
    }

    window.setTimeout(() => {
      const target = fieldRefs.current[firstKey];
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = target.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (focusable ?? target).focus({ preventScroll: true });
    }, 80);
  }, [isMultiStep, visibleSteps]);

  const goToStep = useCallback(
    (target: number, options?: { allowForwardJump?: boolean; source?: RuntimeGoToStepSource }) => {
      if (target < 0 || target >= totalSteps) return;
      // Only allow forward jumps to a previously completed step (or in edit mode).
      const previousVisibleStepId = visibleSteps[target - 1]?.section.sectionId;
      const source = options?.source ?? 'system';
      if (
        target > currentStep
        && previousVisibleStepId
        && !completedStepIds.has(previousVisibleStepId)
        && !isEditMode
        && !options?.allowForwardJump
      ) {
        if (source === 'step-chip') {
          setFormError({ message: 'Complete the current step before jumping ahead.' });
        }
        return;
      }
      const targetStepId = visibleSteps[target]?.section.sectionId;
      if (targetStepId) setActiveStepId(targetStepId);
      // Subtle scroll-to-top so users see the new step's header.
      requestAnimationFrame(() => {
        const container = document.querySelector('[data-runtime-form-scroll]');
        if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    },
    [totalSteps, currentStep, visibleSteps, completedStepIds, isEditMode],
  );

  const handleNext = useCallback(() => {
    if (!isMultiStep) return;
    const nextErrors = collectValidationErrors(activeStepFields);
    applyValidationErrors(nextErrors, activeStepFields);
    if (Object.keys(nextErrors).length > 0) {
      setFormError({
        message: `You have ${Object.keys(nextErrors).length} error${Object.keys(nextErrors).length === 1 ? '' : 's'}. Please fix the highlighted field${Object.keys(nextErrors).length === 1 ? '' : 's'} before continuing.`,
      });
      scrollToFirstError(nextErrors);
      return;
    }
    setFormError(null);
    setCompletedStepIds((prev) => {
      const next = new Set(prev);
      if (activeStep) next.add(activeStep.section.sectionId);
      return next;
    });
    goToStep(currentStep + 1, { allowForwardJump: true, source: 'next' });
  }, [isMultiStep, collectValidationErrors, applyValidationErrors, activeStepFields, activeStep, currentStep, goToStep, scrollToFirstError]);

  const handleBack = useCallback(() => {
    if (currentStep === 0) return;
    goToStep(currentStep - 1, { source: 'back' });
  }, [currentStep, goToStep]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationScope = isMultiStep
      ? visibleSteps.flatMap((step) => step.fields)
      : undefined;
    const nextErrors = collectValidationErrors(validationScope);
    applyValidationErrors(nextErrors, validationScope);
    if (Object.keys(nextErrors).length > 0) {
      setFormError({
        message: `You have ${Object.keys(nextErrors).length} error${Object.keys(nextErrors).length === 1 ? '' : 's'}. Please fix the highlighted field${Object.keys(nextErrors).length === 1 ? '' : 's'} before submitting.`,
      });
      scrollToFirstError(nextErrors);
      return;
    }
    if (!formId || !programId || !contextKey) {
      setFormError({ message: 'Form configuration is not available. Please retry loading the form.' });
      return;
    }
    // ── Patient create: derive effective parent ids ──────────────────────
    // URL query params (set by workspace Add-Patient button) take priority.
    // If Hospital or HCP were not pre-selected (e.g. CC "Add Patient" without
    // a prior workspace selection), fall back to the values the user chose via
    // ENTITY_SELECTOR fields in the registration form itself. Presence is
    // whatever the program's form requires (its own required-field markers
    // already gate this upstream); when neither supplies a value, the entity
    // is created without one — the backend allows that (same as self-registration)
    // and still validates any Hospital/HCP that IS provided.
    let effectiveParentIds = withLegacyEntityParentKeys(parentIds);
    if (contextKey === 'patient' && !isEditMode) {
      const normalized = normalizePatientParentIds(parentIds, formData, formSchema?.fields);
      effectiveParentIds = withLegacyEntityParentKeys(normalized);
    }
    if ((contextKey === 'hcp' || contextKey === 'pharmacy') && !isEditMode) {
      const normalizedHospital = normalizeHospitalParentIds(parentIds, formData, formSchema?.fields);
      effectiveParentIds = withLegacyEntityParentKeys(normalizedHospital);
    }

    setFormError(null);
    submitMutation.mutate({
      programId,
      context: contextKey,
      mode: isEditMode ? 'edit' : 'create',
      entityId: entityId ?? null,
      formId,
      formVersion: formVersion ?? 1,
      formData,
      parentIds: effectiveParentIds,
    });
  };

  // ── Guards ───────────────────────────────────────────────────────────────
  if (!validContext || !config) {
    return (
      <div className="p-4 space-y-3 animate-in fade-in slide-in-from-bottom-1 duration-200">
        <p className="text-sm text-destructive">
          Unknown runtime context: <code>{contextRaw}</code>. Expected one of:{' '}
          {Object.keys(CONTEXT_MAP).join(', ')}.
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Close
        </Button>
      </div>
    );
  }

  if (!validMode) {
    return (
      <div className="p-4 space-y-3 animate-in fade-in slide-in-from-bottom-1 duration-200">
        <p className="text-sm text-destructive">
          Invalid mode: <code>{modeRaw}</code>. Expected "create" or "edit".
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Close
        </Button>
      </div>
    );
  }

  if (isEditMode && !config.supportsEdit) {
    return (
      <div className="p-4 space-y-3 animate-in fade-in slide-in-from-bottom-1 duration-200">
        <p className="text-sm text-destructive">
          Edit mode is not yet supported for <code>{config.label}</code>.
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Close
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 animate-in fade-in duration-200">
        <FormSkeleton fields={8} columns={2} />
      </div>
    );
  }

  if (formResolutionError || noFormAvailable || !formSchema || !Array.isArray(formSchema.fields)) {
    return (
      <div className="p-4 space-y-4 animate-in fade-in slide-in-from-bottom-1 duration-200">
        <EntityFormErrorState
          entityType={(config.entityType ?? 'HOSPITAL') as 'HOSPITAL' | 'HCP' | 'PATIENT'}
          noFormAvailable={noFormAvailable || !formSchema}
          errorMessage={
            formResolutionError instanceof Error ? formResolutionError.message : undefined
          }
        />
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Close
        </Button>
        {Boolean(formResolutionError) && (
          <FormErrorBanner
            message="Failed to load form data."
            onRetry={() => refetchFormResolution()}
          />
        )}
      </div>
    );
  }

  if (entityDetailQuery.error) {
    return (
      <div className="p-4 space-y-4 animate-in fade-in slide-in-from-bottom-1 duration-200">
        <FormErrorBanner
          message="Failed to load the existing record."
          onRetry={() => entityDetailQuery.refetch()}
        />
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Close
        </Button>
      </div>
    );
  }

  const { label, Icon } = config;
  const pageKey = isEditMode ? config.pageInstructionKey.edit : config.pageInstructionKey.create;
  const formDisplayName = resolution?.formDisplayName ?? resolution?.formName ?? label;
  const formVersionLabel = resolution?.formVersion ? `v${resolution.formVersion}` : '';

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-1 duration-200">
      {/* Header */}
      <div
        className={`sticky top-0 z-10 flex flex-shrink-0 items-center justify-between border-b bg-surface-card transition-shadow ${
          compact ? 'px-6 py-4' : 'p-4'
        } ${isBodyScrolled ? 'shadow-sm' : ''}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-primary-soft flex items-center justify-center flex-shrink-0">
            <Icon className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate">
              {isEditMode ? `Edit ${label}` : `New ${label}`}
            </h1>
            {formDisplayName && (
              <p className="text-[11px] text-muted-foreground truncate">
                {formDisplayName} {formVersionLabel}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {submitMutation.isPending && (
            <span className="text-[11px] text-muted-foreground">Saving...</span>
          )}
          <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs">
            Cancel
          </Button>
          {isMultiStep && currentStep < totalSteps - 1 ? (
            <Button
              size="sm"
              onClick={handleNext}
              className="h-8 text-xs"
            >
              Next
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSubmit}
              loading={submitMutation.isPending}
              className="h-8 text-xs"
            >
              {submitMutation.isPending ? (
                isEditMode ? 'Saving…' : 'Creating…'
              ) : (
                <>
                  <Save className="w-3 h-3 mr-1.5" />
                  {isEditMode ? 'Save Changes' : `Create ${label}`}
                </>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            aria-label="Close form"
          >
            <AppIcon icon={X} size="compact" label="Close form" />
          </Button>
        </div>
      </div>

      <div
        data-runtime-form-scroll
        className={`flex-1 min-h-0 overflow-y-auto scroll-smooth ${compact ? 'px-6 py-6' : ''}`}
        onScroll={(event) => setIsBodyScrolled(event.currentTarget.scrollTop > 0)}
      >
        {/* Page Instruction Banner — full-page only */}
        {!compact && (
          <div className="px-4 pt-3">
            <PageInstructionBanner pageRouteKey={pageKey} programId={programId} />
          </div>
        )}

        {formError && (
          <div className={compact ? '' : 'px-4 pt-3'}>
            <FormErrorBanner
              message={formError.message}
              correlationId={formError.correlationId}
              onRetry={formError.message.includes('load') ? () => refetchFormResolution() : undefined}
            />
          </div>
        )}

      {/* Step progress bar + step indicator (multi-step only) */}
      {isMultiStep && (
        <div className={`${compact ? 'pb-4' : 'px-4 pt-3'} space-y-2`}>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              Step {currentStep + 1} of {totalSteps}
              <span className="mx-2 text-foreground/30">·</span>
              <span className="text-foreground font-medium">
                {activeStep?.section.title ?? 'No visible steps'}
              </span>
              {activeStep?.section.sectionId === recommendedStepId && (
                <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                  Recommended
                </span>
              )}
            </span>
            <span className="font-medium text-foreground">{progressPct}%</span>
          </div>
          <progress
            className="w-full h-1 rounded-full overflow-hidden accent-primary bg-muted"
            value={progressPct}
            max={100}
            aria-label="Step progress"
          />
          {/* Step chips */}
          <ol className="flex flex-wrap items-center gap-1.5 pt-1">
            {visibleSteps.map((s, idx) => {
              const isCurrent = idx === currentStep;
              const isComplete = completedStepIds.has(s.section.sectionId);
              const isRecommended = s.section.sectionId === recommendedStepId;
              const reachable = idx <= currentStep || isComplete || isEditMode;
              return (
                <li key={s.section.sectionId}>
                  <button
                    type="button"
                    onClick={() => goToStep(idx, { source: 'step-chip' })}
                    className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] transition-colors ${
                      isCurrent
                        ? 'bg-primary text-primary-foreground font-medium'
                        : isComplete
                          ? 'bg-primary/10 text-primary hover:bg-primary/15'
                          : reachable
                            ? 'bg-muted text-muted-foreground hover:bg-muted/80'
                            : 'bg-muted/50 text-muted-foreground/60 hover:bg-muted/60'
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full text-[11px] font-semibold ${
                        isCurrent
                          ? 'bg-primary-foreground/20'
                          : isComplete
                            ? 'bg-primary/20'
                            : 'bg-background'
                      }`}
                    >
                      {isComplete && !isCurrent ? <Check className="w-3 h-3" /> : idx + 1}
                    </span>
                    <span className="truncate max-w-[140px]">{s.section.title}</span>
                    {isRecommended && !isCurrent && (
                      <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                        Recommended
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
          {recommendedStepId && activeStep?.section.sectionId !== recommendedStepId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => {
                const idx = visibleSteps.findIndex((step) => step.section.sectionId === recommendedStepId);
                if (idx >= 0) goToStep(idx, { allowForwardJump: true });
              }}
            >
              Follow recommended path
            </Button>
          )}
        </div>
      )}

      {/* Form */}
      <div
        className={`${compact ? '' : 'p-4'}`}
      >
        <AppCard>
          <AppCardHeader className="p-4 pb-3">
            <AppCardTitle className="text-sm">
              {isMultiStep ? activeStep?.section.title ?? 'No visible steps' : `${label} Information`}
            </AppCardTitle>
            {isMultiStep && activeStep?.section.description && (
              <p className="text-[11px] text-muted-foreground mt-1">
                {activeStep.section.description}
              </p>
            )}
          </AppCardHeader>
          <AppCardContent className="p-4 pt-0">
            <form onSubmit={handleSubmit}>
              <div
                key={isMultiStep ? `step-${currentStep}` : 'all'}
                className="grid grid-cols-12 gap-3 animate-in fade-in slide-in-from-right-1 duration-200"
              >
                {shouldRenderHospitalSelector && (
                  <div className="contents animate-in fade-in slide-in-from-bottom-1 duration-200">
                    <DynamicFormField
                      field={runtimeHospitalSelectorField}
                      value={formData.hospitalId ?? getFieldDefaultValue(runtimeHospitalSelectorField)}
                      onChange={handleFieldChange}
                      error={errors.hospitalId}
                      formValues={formData}
                      dependencyFieldKeyMap={dependencyFieldKeyMap}
                      programId={programId}
                      fieldRef={(node) => registerFieldRef('hospitalId', node)}
                    />
                  </div>
                )}
                {(isMultiStep ? activeStepFields : getVisibleFields((Array.isArray(formSchema?.fields) ? [...formSchema.fields] : []).sort(
                  (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
                )))
                  .map((field) => (
                    <div
                      key={field.fieldId}
                      className="contents animate-in fade-in slide-in-from-bottom-1 duration-200"
                    >
                      <DynamicFormField
                        field={field}
                        value={formData[field.internalName] ?? getFieldDefaultValue(field)}
                        onChange={handleFieldChange}
                        error={errors[field.internalName]}
                        disabled={!logic.isEnabled(field.fieldId)}
                        formValues={formData}
                        dependencyFieldKeyMap={dependencyFieldKeyMap}
                        programId={programId}
                        fieldRef={(node) => registerFieldRef(field.internalName, node)}
                      />
                      {field.visibilityCondition && logic.isRequired(field) && (
                        <p className="col-span-12 -mt-2 text-[11px] text-muted-foreground">
                          This field is required based on your previous answer.
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </form>
          </AppCardContent>
        </AppCard>

        {/* Footer step navigation (mirrors the header next/submit for long forms) */}
        {isMultiStep && (
          <div className="flex items-center justify-between mt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="h-8 text-xs"
            >
              <ChevronLeft className="w-3 h-3 mr-1" />
              Back
            </Button>
            {currentStep < totalSteps - 1 ? (
              <Button
                type="button"
                size="sm"
                onClick={handleNext}
                className="h-8 text-xs"
              >
                Next
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={handleSubmit}
                loading={submitMutation.isPending}
                className="h-8 text-xs"
              >
                {submitMutation.isPending ? (
                  isEditMode ? 'Saving…' : 'Creating…'
                ) : (
                  <>
                    <Save className="w-3 h-3 mr-1.5" />
                    {isEditMode ? 'Save Changes' : `Create ${label}`}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

